// Rule-based brain for the site chatbot. Pure functions (no React) so it can be tested on its own.
//
// reply(input, ctx) -> { text, link?, products?, chips? }
//   ctx = { faqs, fallback, products, vars, quickReplies, currency }
//   - faqs:   [{ title, keywords: "a, b, c", answer, link, linkLabel }]   (edited in Admin → Home → Chatbot)
//   - vars:   values for {phone} {email} {address} {hours} placeholders in answers
//   - products: the product list from the shop, used for product / budget questions

const STOPWORDS = new Set((
  'a an the i me my we you your is are was were do does did can could would should will have has had of for to in on at with and or but ' +
  'any some what which who how when where why please show find want need looking look buy get got tell about info information there here it its this that ' +
  'than less more under below over above around about budget max up ' +
  'anything something everything cheap cheaper cheapest affordable popular best top good nice great one ones product products item items models range options option eur euro euros'
).split(' '));

// words that mean the customer wants parts / accessories rather than a vehicle
const PART_WORDS = ['part', 'parts', 'spare', 'spares', 'tyre', 'tyres', 'tire', 'tires', 'tube', 'tubes', 'brake', 'brakes', 'pad', 'pads', 'battery', 'charger', 'helmet', 'lock', 'bag', 'accessory', 'accessories', 'inner', 'outer'];

export const normalize = (s) => ` ${String(s || '').toLowerCase().replace(/[^a-z0-9€£.\s']/g, ' ').replace(/\s+/g, ' ').trim()} `;
const tokens = (s) => normalize(s).trim().split(' ').filter(t => t && !STOPWORDS.has(t));

// ── FAQ matching ─────────────────────────────────────────────────────────────
// Each comma-separated keyword/phrase found in the message scores (longer phrases score more).
export function scoreFaq(faq, text) {
  const t = normalize(text);
  let score = 0;
  for (const raw of String(faq.keywords || '').split(',')) {
    const k = raw.trim().toLowerCase();
    if (!k) continue;
    if (t.includes(` ${k} `) || (k.length > 3 && t.includes(` ${k}`))) score += 1 + k.split(' ').length;
  }
  return score;
}
export function matchFaq(faqs, text) {
  let best = null, bestScore = 0;
  for (const f of faqs || []) {
    const s = scoreFaq(f, text);
    if (s > bestScore) { best = f; bestScore = s; }
  }
  return { faq: best, score: bestScore };
}

// ── Product search ───────────────────────────────────────────────────────────
const CATEGORY_WORDS = {
  'electric-scooters': ['scooter', 'scooters', 'escooter', 'e-scooter'],
  'electric-bikes': ['bike', 'bikes', 'ebike', 'ebikes', 'e-bike', 'e-bikes', 'bicycle', 'cycle'],
  'kids-section': ['kids', 'kid', 'children', 'child', 'toddler'],
  'mobility-scooters': ['mobility'],
  'spare-parts': ['parts', 'spare', 'spares', 'battery', 'charger', 'tyre', 'tire'],
  accessories: ['accessories', 'accessory', 'helmet', 'lock', 'bag'],
};
const CATEGORY_LABEL = {
  'electric-scooters': 'electric scooters', 'electric-bikes': 'electric bikes', 'kids-section': 'kids’ scooters and bikes',
  'mobility-scooters': 'mobility scooters', 'spare-parts': 'spare parts', accessories: 'accessories',
};

// "under 500", "below €800", "less than 1000", "max 700", "up to 600"
export function extractBudget(text) {
  const m = String(text).toLowerCase().match(/(?:under|below|less than|max(?:imum)?|up to|within|budget(?: of)?|around|about)\s*[€£]?\s*(\d[\d,]*)/)
    || String(text).match(/[€£]\s*(\d[\d,]*)/);
  if (!m) return null;
  const n = parseInt(m[1].replace(/,/g, ''), 10);
  return n > 0 ? n : null;
}

export function searchProducts(products, text) {
  const list = Array.isArray(products) ? products.filter(p => p && p.name) : [];
  const toks = tokens(text);
  const budget = extractBudget(text);
  const t = normalize(text);

  // which category words did they use?
  const cats = Object.keys(CATEGORY_WORDS).filter(c => CATEGORY_WORDS[c].some(w => t.includes(` ${w} `)));
  const catWordSet = new Set(Object.values(CATEGORY_WORDS).flat());
  // tokens that look like a model / brand name rather than a generic category word
  const nameToks = toks.filter(w => !catWordSet.has(w) && !/^[€£]?\d/.test(w) && w.length > 1 && !['electric', 'euro', 'eur'].includes(w));

  const wantsParts = PART_WORDS.some(w => t.includes(` ${w} `));
  const isPart = (p) => p.category === 'spare-parts' || p.category === 'accessories';

  const scored = list.map(p => {
    const hay = normalize(`${p.name} ${p.brand || ''} ${p.model || ''}`);
    let score = 0;
    for (const w of nameToks) if (hay.includes(` ${w} `) || hay.includes(` ${w}`)) score += 3;
    if (cats.length && cats.includes(p.category)) score += 2;
    // "Kukirin G2" should show the scooter, not its inner tube - unless they asked for parts
    if (score > 0 && isPart(p) && !wantsParts) score -= 2;
    return { p, score };
  });

  let hits = scored.filter(x => x.score > 0);
  if (budget) hits = hits.filter(x => Number(x.p.price || 0) <= budget);
  // a budget on its own ("under 500") = vehicles under that price (parts only if they asked for them)
  if (!hits.length && budget && !nameToks.length) hits = scored.filter(x => Number(x.p.price || 0) <= budget && (wantsParts || !isPart(x.p)));
  hits = hits.filter(x => x.p.inStock !== false).concat(hits.filter(x => x.p.inStock === false));

  // best specific-name match first; with a budget show the dearest that still fits
  hits.sort((a, b) => b.score - a.score || (budget ? Number(b.p.price) - Number(a.p.price) : Number(a.p.price) - Number(b.p.price)));
  const specific = hits.length ? hits[0].score >= 3 : false;
  return { hits: hits.map(h => h.p), specific, categories: cats, budget, hasNameTokens: nameToks.length > 0 };
}

const toCard = (p, currency = '€') => ({
  id: p._id || p.id || p.model,
  name: p.name,
  price: `${currency}${Number(p.price || 0).toFixed(2)}`,
  oldPrice: Number(p.oldPrice) > Number(p.price) ? `${currency}${Number(p.oldPrice).toFixed(2)}` : '',
  image: p.images?.[0] || p.image || '',
  to: `/products/${p.category}/${p.model || p._id}`,
  inStock: p.inStock !== false,
});

// ── small talk ───────────────────────────────────────────────────────────────
const GREETING = /^\s*(hi+|hello+|hey+|hiya|howdy|yo|good (morning|afternoon|evening)|hola|dia dhuit)\b/i;
const THANKS = /\b(thanks|thank you|thx|cheers|much appreciated|brilliant|perfect|great,? thanks)\b/i;
const BYE = /^\s*(bye|goodbye|see you|that'?s all|no thanks|nope|nothing else)\b/i;

// Customer asks for a person / wants to leave a message → offer to send it to the team (same as the Contact Us form)
const SUPPORT_INTENT = new RegExp(
  '\\b(speak|talk|chat|connect|get)\\b[^.?!]{0,25}\\b(someone|somebody|person|human|agent|team|staff|representative|advisor|adviser|support)\\b' +
  '|\\b(human|real person|representative|customer (support|service)|support (team|agent)|call me back|callback|call back|' +
  'leave (a )?message|message (the |your )?team|email (the |your )?team|complaint|complain|raise (a )?(ticket|complaint)|' +
  'someone (to )?(call|contact|email|ring)|ring me|get back to me)\\b', 'i');

export const fillVars = (text, vars = {}) =>
  String(text || '').replace(/\{(\w+)\}/g, (m, k) => (vars[k] !== undefined && vars[k] !== '' ? vars[k] : m === '{phone}' ? 'the shop' : ''));

// ── main entry ───────────────────────────────────────────────────────────────
export function reply(input, ctx = {}) {
  const { faqs = [], fallback = '', products = [], vars = {}, quickReplies = [], currency = '€', supportEnabled = true,
    supportIntro = 'I’ll pass this to our team. Leave your details and they’ll get back to you by email or phone.' } = ctx;
  const text = String(input || '').trim();
  const chips = quickReplies;
  if (!text) return null;

  if (GREETING.test(text) && text.split(/\s+/).length <= 4) return { text: 'Hello! 👋 How can I help you today?', chips };
  if (THANKS.test(text) && text.split(/\s+/).length <= 6) return { text: 'You’re very welcome! Is there anything else I can help with?', chips };
  if (BYE.test(text)) return { text: 'Thanks for chatting — have a great day! 🛴', chips: [] };

  // "Can I speak to someone?" → don't recite a phone number, offer to take a message for the team
  if (supportEnabled && SUPPORT_INTENT.test(text)) {
    return { text: fillVars(supportIntro, vars), handoff: true, asked: text, chips: [] };
  }

  const { faq, score: faqScore } = matchFaq(faqs, text);
  const found = searchProducts(products, text);

  // A specific product / model name beats a generic FAQ word
  if (found.specific && found.hits.length && found.hasNameTokens) {
    const top = found.hits.slice(0, 3).map(p => toCard(p, currency));
    return { text: top.length === 1 ? 'I found this one for you:' : 'Here’s what I found:', products: top, link: { label: 'See all products', to: '/products' }, chips };
  }

  if (faq && faqScore > 0) {
    return { text: fillVars(faq.answer, vars), link: faq.link ? { label: faq.linkLabel || 'Learn more', to: faq.link } : null, handoff: !!faq.handoff && supportEnabled, chips };
  }

  // category and/or budget question ("electric scooters", "bikes under 800")
  if (found.categories.length || found.budget) {
    const cat = found.categories[0];
    const label = cat ? CATEGORY_LABEL[cat] : 'products';
    const top = found.hits.slice(0, 3).map(p => toCard(p, currency));
    if (top.length) {
      const intro = found.budget ? `Here ${top.length === 1 ? 'is a' : 'are some'} ${label} up to ${currency}${found.budget}:` : `Here are some of our ${label}:`;
      return { text: intro, products: top, link: { label: cat ? `All ${label}` : 'All products', to: cat ? `/products?category=${cat}` : '/products' }, chips };
    }
    return { text: found.budget ? `I couldn’t find ${label} up to ${currency}${found.budget}. Take a look at the full range:` : `Take a look at our ${label}:`, link: { label: 'Browse products', to: cat ? `/products?category=${cat}` : '/products' }, chips };
  }

  if (found.hits.length && found.hasNameTokens) {
    const top = found.hits.slice(0, 3).map(p => toCard(p, currency));
    return { text: 'Here’s what I found:', products: top, link: { label: 'See all products', to: '/products' }, chips };
  }

  // Couldn't answer: this is exactly when a person should step in
  return { text: fillVars(fallback, vars), fallback: true, handoff: supportEnabled, chips };
}

// Plain-text transcript of the last few messages, appended to an enquiry so the team has context
export function buildTranscript(messages, max = 6) {
  const clip = (s, n = 200) => { const t = String(s || '').replace(/\s+/g, ' ').trim(); return t.length > n ? t.slice(0, n - 1) + '…' : t; };
  return (messages || [])
    .filter(m => m && !m.form && (m.text || m.products?.length))
    .slice(-max)
    .map(m => `${m.from === 'user' ? 'Customer' : 'Bot'}: ${clip(m.text || (m.products || []).map(p => p.name).join(', '))}`)
    .join('\n');
}

// The message that is sent to the team: the customer's words, a marker, and recent chat context
export function buildEnquiryMessage(message, messages) {
  const transcript = buildTranscript(messages);
  const head = String(message || '').trim().slice(0, 1500);
  return `${head}\n\n— Sent from the website chatbot —${transcript ? `\nRecent chat:\n${transcript}` : ''}`.slice(0, 2400);
}
